import React, { useEffect, useState } from 'react';
import {
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  NavbarMenuToggle,
  NavbarMenu,
  NavbarMenuItem,
  Link,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Avatar,
} from '@nextui-org/react';
import {
  Search,
  Settings,
  ChevronDown,
  Trophy,
  Users,
  Beer,
  ShoppingBag,
  CalendarDays,
  BarChart3,
  Mail,
  User as UserIcon,
  LogOut,
  X,
  Heart,
  Sliders,
  Dices,
  MessageCircle,
  ClipboardList,
} from 'lucide-react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSearchExplorer } from '../contexts/SearchExplorerContext';
import userService from '../services/userService';
import chatService from '../services/chatService';

const APP_BANNER_DISMISSED_KEY = 'dc-app-banner-dismissed';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showAppBanner, setShowAppBanner] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { isAuthenticated, user, logout } = useAuth();
  const { open: openSearch } = useSearchExplorer();
  const navigate = useNavigate();

  // Ungelesene Nachrichten pollen (chatService fängt Fehler ab und liefert 0)
  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      return;
    }
    let cancelled = false;
    const poll = () => {
      chatService
        .getUnreadCount()
        .then((count) => {
          if (!cancelled) setUnreadCount(count ?? 0);
        })
        .catch(() => {
          /* ignorieren */
        });
    };
    poll();
    const timer = setInterval(poll, 30000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(APP_BANNER_DISMISSED_KEY);
      if (dismissed !== 'true') setShowAppBanner(true);
    } catch {
      setShowAppBanner(true);
    }
  }, []);

  const dismissAppBanner = () => {
    setShowAppBanner(false);
    try {
      localStorage.setItem(APP_BANNER_DISMISSED_KEY, 'true');
    } catch {
      // ignore storage errors
    }
  };

  const displayName =
    [user?.firstname, user?.lastname].filter(Boolean).join(' ') ||
    user?.username ||
    user?.email ||
    'Mein Konto';
  const initials = (() => {
    const f = (user?.firstname || '').charAt(0);
    const l = (user?.lastname || '').charAt(0);
    const combined = `${f}${l}`.toUpperCase();
    if (combined) return combined;
    return (user?.username || user?.email || '?').charAt(0).toUpperCase();
  })();
  const avatarUrl = userService.getFileUrl(user?.profileImage);

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  const menuItems = [
    { name: 'Spiele', href: '/spiele' },
    { name: 'In der Nähe', href: '/in-der-naehe' },
    { name: 'Community', href: '/community' },
    { name: 'Statistiken', href: '/statistiken' }
  ];

  const moreDropdownItems = [
    {
      name: 'Turniere',
      href: '/turniere',
      description: 'Dart-Turniere finden',
      icon: Trophy,
    },
    {
      name: 'Vereine',
      href: '/vereine',
      description: 'Dart-Vereine in deiner Nähe',
      icon: Users,
    },
    {
      name: 'Spielstätten',
      href: '/spielstaetten',
      description: 'Kneipen & Bars mit Dart',
      icon: Beer,
    },
    {
      name: 'Shops',
      href: '/shops',
      description: 'Dart-Equipment kaufen',
      icon: ShoppingBag,
    },
    {
      name: 'Turnierserien',
      href: '/turnierserien',
      description: 'Regelmäßige Turnierserien',
      icon: CalendarDays,
    },
    {
      name: 'Ranglisten',
      href: '/ranglisten',
      description: 'Pro-Spieler Rankings',
      icon: BarChart3,
    },
    {
      name: 'Tippspiele',
      href: '/tippspiele',
      description: 'Tippen & Punkte sammeln',
      icon: Dices,
    },
    {
      name: 'Kontakt',
      href: '/kontakt',
      description: 'Kontakt aufnehmen',
      icon: Mail,
    },
  ];

  return (
    <>
      <Navbar
        isMenuOpen={isMenuOpen}
        onMenuOpenChange={setIsMenuOpen}
        className="bg-black/95 backdrop-blur-md border-b border-zinc-800"
        maxWidth="full"
        height="4rem"
      >
        <NavbarContent>
          <NavbarMenuToggle
            aria-label={isMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
            className="sm:hidden text-white"
          />
          <NavbarBrand className="flex-grow-0">
            <RouterLink to="/" aria-label="Zur Startseite" className="flex items-center">
              <img
                src="/DC_Logo_Typo_Blau_gross.png"
                alt="DartCloud Logo"
                className="h-7 sm:h-8 md:h-9 w-auto max-w-[120px] sm:max-w-[140px] md:max-w-[160px] object-contain"
              />
            </RouterLink>
          </NavbarBrand>
        </NavbarContent>

        <NavbarContent className="hidden sm:flex gap-8" justify="center">
          {menuItems.map((item) => (
            <NavbarItem key={item.name}>
              <Link 
                color="foreground" 
                href={item.href}
                className="text-gray-300 hover:text-white transition-colors"
              >
                {item.name}
              </Link>
            </NavbarItem>
          ))}
          
          {/* Mehr Dropdown */}
          <NavbarItem>
            <Dropdown
              placement="bottom"
              classNames={{
                content:
                  'p-0 bg-zinc-950/95 backdrop-blur-xl border border-zinc-800 shadow-2xl shadow-black/60 rounded-2xl',
              }}
            >
              <DropdownTrigger>
                <button
                  type="button"
                  className="group inline-flex items-center gap-1 bg-transparent p-0 border-0 text-gray-300 hover:text-white transition-colors focus:outline-none"
                >
                  <span>Mehr</span>
                  <ChevronDown
                    size={16}
                    className="transition-transform duration-200 group-data-[open=true]:rotate-180"
                  />
                </button>
              </DropdownTrigger>
              <DropdownMenu
                aria-label="Mehr Menü"
                className="w-[560px] p-3 grid grid-cols-2 gap-1"
                itemClasses={{
                  base: [
                    'rounded-xl',
                    'px-3 py-2.5',
                    'transition-colors',
                    'data-[hover=true]:bg-zinc-800/70',
                    'data-[selectable=true]:focus:bg-zinc-800/70',
                  ].join(' '),
                }}
              >
                {moreDropdownItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownItem
                      key={item.name}
                      href={item.href}
                      textValue={item.name}
                      startContent={
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#3fced1]/10 text-[#3fced1]">
                          <Icon size={18} />
                        </span>
                      }
                    >
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-white">
                          {item.name}
                        </span>
                        <span className="text-xs text-gray-400">
                          {item.description}
                        </span>
                      </div>
                    </DropdownItem>
                  );
                })}
              </DropdownMenu>
            </Dropdown>
          </NavbarItem>
        </NavbarContent>

        <NavbarContent justify="end" className="gap-2">
          <NavbarItem>
            <button
              type="button"
              aria-label="Suche öffnen"
              onClick={openSearch}
              className="inline-flex items-center justify-center text-gray-400 hover:text-white transition-colors"
            >
              <Search size={20} />
            </button>
          </NavbarItem>

          {isAuthenticated ? (
            <NavbarItem>
              <Dropdown placement="bottom-end">
                <DropdownTrigger>
                  <Button
                    variant="light"
                    className="text-white gap-2 px-2"
                    startContent={
                      <Avatar
                        src={avatarUrl || undefined}
                        name={initials}
                        showFallback
                        size="sm"
                        className="h-8 w-8 bg-[#3fced1] text-black text-sm font-semibold"
                      />
                    }
                    endContent={<ChevronDown size={16} className="text-gray-400" />}
                  >
                    <span className="hidden md:inline max-w-[140px] truncate text-sm">
                      {displayName}
                    </span>
                  </Button>
                </DropdownTrigger>
                <DropdownMenu aria-label="Konto">
                  <DropdownItem key="profile" startContent={<UserIcon size={16} />}
                    onPress={() => navigate('/profil')}>
                    Profil
                  </DropdownItem>
                  <DropdownItem key="favorites" startContent={<Heart size={16} />}
                    onPress={() => navigate('/favoriten')}>
                    Favoriten
                  </DropdownItem>
                  <DropdownItem key="registrations" startContent={<ClipboardList size={16} />}
                    onPress={() => navigate('/meine-anmeldungen')}>
                    Meine Anmeldungen
                  </DropdownItem>
                  <DropdownItem key="messages" startContent={<MessageCircle size={16} />}
                    onPress={() => navigate('/nachrichten')}>
                    {unreadCount > 0 ? `Nachrichten (${unreadCount})` : 'Nachrichten'}
                  </DropdownItem>
                  <DropdownItem key="management" startContent={<Sliders size={16} />}
                    onPress={() => navigate('/verwaltung')}>
                    Verwaltung
                  </DropdownItem>
                  <DropdownItem key="settings" startContent={<Settings size={16} />}
                    onPress={() => navigate('/einstellungen')}>
                    Einstellungen
                  </DropdownItem>
                  <DropdownItem
                    key="logout"
                    color="danger"
                    startContent={<LogOut size={16} />}
                    onPress={handleLogout}
                  >
                    Abmelden
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </NavbarItem>
          ) : (
            <>
              <NavbarItem>
                <Dropdown placement="bottom-end">
                  <DropdownTrigger>
                    <Button
                      isIconOnly
                      variant="light"
                      className="text-gray-400 hover:text-white"
                      aria-label="Einstellungen"
                    >
                      <Settings size={20} />
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu aria-label="Einstellungen">
                    <DropdownItem key="help" onPress={() => navigate('/hilfe')}>
                      Hilfe
                    </DropdownItem>
                    <DropdownItem key="contact" onPress={() => navigate('/kontakt')}>
                      Kontakt
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </NavbarItem>

              <NavbarItem className="hidden lg:flex gap-2">
                <Button
                  as={RouterLink as any}
                  to="/login"
                  color="default"
                  variant="light"
                  className="text-gray-300 hover:text-white"
                >
                  Einloggen
                </Button>
                <Button
                  as={RouterLink as any}
                  to="/register"
                  color="primary"
                  variant="solid"
                  className="bg-[#3fced1] hover:bg-[#35b5b8] text-black"
                >
                  Konto erstellen
                </Button>
              </NavbarItem>
            </>
          )}
        </NavbarContent>

        <NavbarMenu
          className="bg-black/95 backdrop-blur-md pt-6"
          onClick={() => setIsMenuOpen(false)}
        >
          {menuItems.map((item, index) => (
            <NavbarMenuItem key={`${item.name}-${index}`}>
              <Link
                className="w-full text-gray-300 hover:text-white"
                href={item.href}
                size="lg"
              >
                {item.name}
              </Link>
            </NavbarMenuItem>
          ))}

          <NavbarMenuItem className="mt-4">
            <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">
              Mehr entdecken
            </p>
          </NavbarMenuItem>
          {moreDropdownItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavbarMenuItem key={`mobile-${item.name}`}>
                <RouterLink
                  to={item.href}
                  className="flex items-center gap-3 py-2 text-gray-300 hover:text-white"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#3fced1]/10 text-[#3fced1]">
                    <Icon size={16} />
                  </span>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{item.name}</span>
                    <span className="text-xs text-gray-500">{item.description}</span>
                  </div>
                </RouterLink>
              </NavbarMenuItem>
            );
          })}

          {isAuthenticated ? (
            <>
              <NavbarMenuItem className="mt-4">
                <div className="flex items-center gap-3 py-2">
                  <Avatar
                    src={avatarUrl || undefined}
                    name={initials}
                    showFallback
                    className="h-10 w-10 bg-[#3fced1] text-black font-semibold"
                  />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-white">{displayName}</span>
                    {user?.email && (
                      <span className="text-xs text-gray-400">{user.email}</span>
                    )}
                  </div>
                </div>
              </NavbarMenuItem>
              <NavbarMenuItem>
                <RouterLink
                  to="/profil"
                  className="flex items-center gap-3 py-2 text-gray-200 hover:text-white"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#3fced1]/10 text-[#3fced1]">
                    <UserIcon size={16} />
                  </span>
                  <span className="text-sm font-medium">Profil</span>
                </RouterLink>
              </NavbarMenuItem>
              <NavbarMenuItem>
                <RouterLink
                  to="/favoriten"
                  className="flex items-center gap-3 py-2 text-gray-200 hover:text-white"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#3fced1]/10 text-[#3fced1]">
                    <Heart size={16} />
                  </span>
                  <span className="text-sm font-medium">Favoriten</span>
                </RouterLink>
              </NavbarMenuItem>
              <NavbarMenuItem>
                <RouterLink
                  to="/meine-anmeldungen"
                  className="flex items-center gap-3 py-2 text-gray-200 hover:text-white"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#3fced1]/10 text-[#3fced1]">
                    <ClipboardList size={16} />
                  </span>
                  <span className="text-sm font-medium">Meine Anmeldungen</span>
                </RouterLink>
              </NavbarMenuItem>
              <NavbarMenuItem>
                <RouterLink
                  to="/nachrichten"
                  className="flex items-center gap-3 py-2 text-gray-200 hover:text-white"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#3fced1]/10 text-[#3fced1]">
                    <MessageCircle size={16} />
                  </span>
                  <span className="text-sm font-medium">
                    {unreadCount > 0 ? `Nachrichten (${unreadCount})` : 'Nachrichten'}
                  </span>
                </RouterLink>
              </NavbarMenuItem>
              <NavbarMenuItem>
                <RouterLink
                  to="/verwaltung"
                  className="flex items-center gap-3 py-2 text-gray-200 hover:text-white"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#3fced1]/10 text-[#3fced1]">
                    <Sliders size={16} />
                  </span>
                  <span className="text-sm font-medium">Verwaltung</span>
                </RouterLink>
              </NavbarMenuItem>
              <NavbarMenuItem>
                <Button
                  color="danger"
                  variant="flat"
                  className="w-full"
                  onPress={handleLogout}
                  startContent={<LogOut size={16} />}
                >
                  Abmelden
                </Button>
              </NavbarMenuItem>
            </>
          ) : (
            <>
              <NavbarMenuItem className="mt-4">
                <Button
                  as={RouterLink as any}
                  to="/login"
                  color="default"
                  variant="light"
                  className="w-full text-gray-300"
                >
                  Einloggen
                </Button>
              </NavbarMenuItem>
              <NavbarMenuItem>
                <Button
                  as={RouterLink as any}
                  to="/register"
                  color="primary"
                  variant="solid"
                  className="w-full bg-[#3fced1] hover:bg-[#35b5b8] text-black"
                >
                  Konto erstellen
                </Button>
              </NavbarMenuItem>
            </>
          )}
        </NavbarMenu>
      </Navbar>

      {/* Hero Banner */}
      {showAppBanner && (
        <div className="bg-gradient-to-r from-[#3fced1]/20 to-[#3fced1]/10 p-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
            <p className="text-sm md:text-base">
              Alle Features nutzen mit der DartCloud App - Turniere, Live-Scores, Statistiken & mehr!
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="solid"
                className="bg-[#3fced1] text-black hover:bg-[#35b5b8] flex items-center gap-1"
                onClick={() => window.open('https://play.google.com/store/apps', '_blank')}
              >
                📱 App Download
              </Button>
              <Button
                isIconOnly
                size="sm"
                variant="light"
                aria-label="Hinweis schließen"
                className="text-gray-500 hover:text-white"
                onPress={dismissAppBanner}
              >
                <X size={16} />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;